import {defaultTo, delay, equals, flow, get} from '@logistics-frontend/utils'

import {ServerError} from '../fetch'
import {FetchFunction, FetchResult, TypedRequestConfig} from '../types'

type CSRFResponse = {
  sk: string
  'max-age-seconds': number
}
type CSRFErrorResponse = {
  code: string // 'INVALID_CSRF_TOKEN'
  message: string // 'Invalid/missing CSRF token'
}

const isInvalidToken: (error: ServerError<CSRFErrorResponse>) => boolean = flow(
  get('body.code'),
  equals('INVALID_CSRF_TOKEN'),
)
const ATTEMPTS_KEY = '__csrf_attempts__'
const getAttempts: (c: TypedRequestConfig) => number = flow(get(`meta.${ATTEMPTS_KEY}`), defaultTo(0))
const setAttempts = (config: TypedRequestConfig, attempts: number): TypedRequestConfig => ({
  ...config,
  meta: {
    ...config.meta,
    [ATTEMPTS_KEY]: attempts,
  },
})

function withCSRF<TReq = unknown, TRes = unknown>(fetchFn: FetchFunction<TReq, TRes>): FetchFunction<TReq, TRes> {
  let _token: string = ''
  let _validUntil: number = -1
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let _promise: Promise<any> | null = null

  const cache = () => {
    const requestTime = Date.now()

    return {
      set(response: FetchResult<CSRFResponse>) {
        const {sk: token, 'max-age-seconds': ttl} = response.body

        // 60 секунд вычитаем для того, чтобы токен не протух во время сетевых ретраев
        _validUntil = requestTime + (ttl - 60) * 1000
        _token = token
        _promise = null
      },
      get(): string | undefined {
        if (_token && _validUntil > requestTime) {
          return _token
        }
      },
      reset(): Promise<void> {
        _validUntil = -1
        _token = ''
        _promise = null

        return delay(300)
      },
    }
  }
  const _withCSRF: FetchFunction<TReq, TRes> = config => {
    const {set, get, reset} = cache()
    const cachedToken = get()

    if (cachedToken) {
      return fetchFn({
        ...config,
        headers: {
          ...config.headers,
          'X-CSRF-Token': cachedToken,
        },
      }).catch(error => {
        const attempts = getAttempts(config)

        if (attempts < 5 && isInvalidToken(error)) {
          return reset().then(() => _withCSRF(setAttempts(config, attempts + 1)))
        }

        throw error
      })
    }

    if (_promise === null) {
      _promise = fetchFn({
        ...config,
        body: undefined,
        url: '/csrf_token',
        method: 'post',
        options: {
          credentials: 'include',
        },
      }) as unknown as Promise<FetchResult<CSRFResponse>>
      _promise.then(set)
    }

    return _promise.then(() => _withCSRF(config))
  }

  return _withCSRF
}

export default withCSRF
